import { useMemo, useRef, useState } from 'react';
import {
  ImportError,
  previewImport,
  saveImport,
  type ImportAnchor,
  type ImportPreview,
  type LibraryEntry,
} from '../api/catalog';
import { ca } from '../i18n/ca';
import { AnchorSketch } from './AnchorSketch';
import { eraLabel, theatresOf, useEras } from './useEras';

const t = ca.catalogPage2.library.workshop;

/** The anchors a start base or a forward post cannot be saved without (AnchorProposal.REQUIRED). */
const REQUIRED = ['spawn', 'flag', 'orders', 'arsenal', 'medic', 'gate', 'park'];

type Tag = { era: string; theatre: string | null };

/** What a refusal or a failed save says, in words. */
function errorText(err: unknown): string {
  if (err instanceof ImportError) {
    const refusal = t.refusals[err.code];
    if (typeof refusal === 'function') return refusal(err.count, err.limit);
    if (typeof refusal === 'string') return refusal;
    if (err.code.startsWith('anchor_missing:')) {
      const name = err.code.slice('anchor_missing:'.length);
      return t.anchorMissing(ca.catalogPage2.library.workshop.anchorNames[name] ?? name);
    }
    return t.saveErrors[err.code] ?? err.code;
  }
  return err instanceof Error ? err.message : String(err);
}

/** The line a check shows: its Catalan sentence, else a neutral line with the key. */
function checkText(key: string, n: number, detail: string): string {
  const fn = t.checks[key];
  if (fn) return fn(n);
  return `${t.unknownCheck(key)}${detail ? ` (${detail})` : ''}`;
}

/**
 * The Workshop import (docs/design/COMPOSITION_LIBRARY.md §5, piece 6): a
 * composition.sqe or mission.sqm and, when there is one, the item's link, read
 * by the platform with the checks of §10; then the review form (name, roles,
 * tags, the anchors proposed and draggable on the sketch, the classes the mod
 * set lacks with «make a variant without them», author, link and licence).
 * What is saved starts disabled and unapproved.
 */
export function WorkshopImport({
  modSetHash,
  onSaved,
}: {
  modSetHash: string | null;
  onSaved?: (entry: LibraryEntry) => void;
}) {
  const eras = useEras();
  const fileInput = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [link, setLink] = useState('');
  const [withoutMissing, setWithoutMissing] = useState(false);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [anchors, setAnchors] = useState<ImportAnchor[]>([]);
  const [name, setName] = useState('');
  const [roles, setRoles] = useState<string[]>(['start_base:camp']);
  const [tags, setTags] = useState<Tag[]>([]);
  const [tagEra, setTagEra] = useState('');
  const [tagTheatre, setTagTheatre] = useState('');
  const [author, setAuthor] = useState('');
  const [licence, setLicence] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function read(chosen: File | null, variant: boolean) {
    if (!chosen) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const p = await previewImport(modSetHash, chosen, link, variant);
      setPreview(p);
      setAnchors(p.anchors);
      if (!p.refusal) {
        setName((n) => n || p.suggestedName);
      }
    } catch (err) {
      setPreview(null);
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const reset = () => {
    setFile(null);
    setPreview(null);
    setAnchors([]);
    setName('');
    setTags([]);
    setAuthor('');
    setLicence('');
    setLink('');
    setWithoutMissing(false);
    setError(null);
  };

  const body = preview?.body ?? null;
  const report = preview?.report ?? null;

  /** The pieces as the anchors now stand: a piece an anchor stands on goes where the anchor was dragged. */
  const shownObjects = useMemo(() => {
    if (!body) return [];
    const moved = new Map<number, ImportAnchor>();
    anchors.forEach((a) => {
      if (a.piece >= 0 && a.placed) moved.set(a.piece, a);
    });
    return body.objects.map((o, i) => {
      const a = moved.get(i);
      return a ? { ...o, offset: [a.x, a.y, o.offset[2] ?? 0] } : o;
    });
  }, [body, anchors]);

  const missingNames = useMemo(
    () =>
      REQUIRED.filter((r) => !anchors.some((a) => a.name === r && a.placed)).map(
        (r) => t.anchorNames[r] ?? r,
      ),
    [anchors],
  );
  const problems = [
    ...(name.trim() ? [] : [t.needName]),
    ...(roles.length > 0 ? [] : [t.needRole]),
    ...(tags.length > 0 ? [] : [t.needTag]),
    ...(missingNames.length > 0 ? [t.needAnchors(missingNames.join(', '))] : []),
  ];

  const move = (nameOf: string, index: number, x: number, y: number) =>
    setAnchors((list) =>
      list.map((a) =>
        a.name === nameOf && a.index === index ? { ...a, x, y, placed: true, how: 'you' } : a,
      ),
    );

  const addTag = () => {
    if (!tagEra) return;
    const next: Tag = { era: tagEra, theatre: tagTheatre || null };
    if (tags.some((g) => g.era === next.era && g.theatre === next.theatre)) return;
    setTags((list) => [...list, next]);
  };

  async function save() {
    if (!file || problems.length > 0) return;
    setBusy(true);
    setError(null);
    try {
      const entry = await saveImport(modSetHash, file, {
        name: name.trim(),
        roles,
        tags,
        author: author.trim(),
        licence: licence.trim(),
        link: link.trim() || null,
        withoutMissing,
        anchors: anchors
          .filter((a) => a.placed)
          .map((a) => ({ name: a.name, index: a.index, x: a.x, y: a.y, azimuth: a.azimuth })),
      });
      setNotice(t.saved(entry.name));
      reset();
      onSaved?.(entry);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const workshop = preview?.workshop ?? null;
  const droppedLines = report
    ? (Object.keys(t.dropped) as (keyof typeof report.dropped)[])
        .filter((k) => typeof report.dropped[k] === 'number' && (report.dropped[k] as number) > 0)
        .map((k) => t.dropped[k as string]((report.dropped[k] as number) ?? 0))
    : [];

  return (
    <div className="card form workshop-import" role="group" aria-label={t.title}>
      <h3>
        <i className="bi bi-cloud-download" aria-hidden="true" /> {t.title}
      </h3>
      <p className="muted small">{t.intro}</p>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {notice && <p className="ok small">{notice}</p>}

      <div className="row wrap workshop-source">
        <label>
          {t.file}
          <input
            ref={fileInput}
            type="file"
            name="composition-file"
            accept=".sqe,.sqm,.txt"
            onChange={(e) => {
              const chosen = e.target.files?.[0] ?? null;
              setFile(chosen);
              setPreview(null);
              setAnchors([]);
              setWithoutMissing(false);
            }}
          />
        </label>
        <label className="grow">
          {t.link}
          <input
            type="text"
            name="workshop-link"
            value={link}
            placeholder="https://steamcommunity.com/sharedfiles/filedetails/?id=…"
            onChange={(e) => setLink(e.target.value)}
          />
        </label>
        <button
          type="button"
          className="primary"
          disabled={!file || busy}
          onClick={() => void read(file, withoutMissing)}
        >
          {busy && !preview ? t.reading : t.read}
        </button>
      </div>
      <p className="muted small">{t.linkHint}</p>

      {preview?.refusal && (
        <p className="error" role="alert">
          {errorText(
            new ImportError(preview.refusal.code, preview.refusal.count, preview.refusal.limit),
          )}
        </p>
      )}

      {workshop && (
        <div className="workshop-item row wrap">
          {workshop.previewUrl && (
            <img
              src={workshop.previewUrl}
              alt={t.previewAlt}
              width={160}
              referrerPolicy="no-referrer"
              loading="lazy"
            />
          )}
          <div>
            {workshop.title && <strong>{workshop.title}</strong>}
            {workshop.updated && (
              <p className="muted small">{t.itemUpdated(workshop.updated.slice(0, 10))}</p>
            )}
            {workshop.error && (
              <p className="small" role="status">
                <span className="chip warn">
                  {t.workshopErrors[workshop.error] ?? workshop.error}
                </span>
              </p>
            )}
            {workshop.link && (
              <a href={workshop.link} target="_blank" rel="noreferrer noopener">
                <i className="bi bi-box-arrow-up-right" aria-hidden="true" /> {t.openItem}
              </a>
            )}
          </div>
        </div>
      )}

      {report && body && (
        <>
          <p>
            <strong>{t.summary(report.read, report.kept)}</strong>
          </p>
          {droppedLines.length > 0 && (
            <p className="muted small">
              {t.leftOut}: {droppedLines.join(' · ')}
            </p>
          )}
          {report.checks.length > 0 && (
            <ul className="list workshop-checks">
              {report.checks.map((c) => (
                <li key={c.key}>
                  <i className="bi bi-exclamation-triangle" aria-hidden="true" />{' '}
                  {checkText(c.key, c.n, c.detail)}
                </li>
              ))}
            </ul>
          )}
          {report.guessedHeights.length > 0 && (
            <details>
              <summary>{t.guessedTitle}</summary>
              <ul className="list">
                {report.guessedHeights.slice(0, 12).map((g, i) => (
                  <li key={i} className="small">
                    {g.className} · {g.height} m
                  </li>
                ))}
              </ul>
            </details>
          )}
          {(report.origins.length > 0 || report.factions.length > 0) && (
            <div className="workshop-origins">
              {report.origins.length > 0 && (
                <>
                  <h4>{t.modsTitle}</h4>
                  <p className="muted small">{t.modsHint}</p>
                  <div className="row wrap">
                    {report.origins.map((o) => {
                      const off = o.eraHint && !tags.some((g) => g.era === o.eraHint);
                      return (
                        <span
                          key={o.mod}
                          className={`chip ${off && tags.length > 0 ? 'warn' : ''}`}
                        >
                          {o.mod} ({o.pieces})
                          {o.eraHint ? ` · ${t.eraHint(eraLabel(eras, o.eraHint))}` : ''}
                        </span>
                      );
                    })}
                  </div>
                </>
              )}
              {report.factions.length > 0 && (
                <>
                  <h4>{t.factionsTitle}</h4>
                  <div className="row wrap">
                    {report.factions.map((f) => (
                      <span key={f.faction} className="chip">
                        {f.faction} ({f.pieces})
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          <h4>{t.missingTitle}</h4>
          {report.missing.length === 0 && report.leftOutMissing.length === 0 && (
            <p className="muted small">{t.missingNone}</p>
          )}
          {report.missing.length > 0 && (
            <div className="row wrap">
              {report.missing.map((m) => (
                <span key={m} className="chip warn">
                  {m}
                </span>
              ))}
            </div>
          )}
          {(report.missing.length > 0 || report.leftOutMissing.length > 0) && (
            <label className="row">
              <input
                type="checkbox"
                name="without-missing"
                checked={withoutMissing}
                disabled={busy}
                onChange={(e) => {
                  setWithoutMissing(e.target.checked);
                  void read(file, e.target.checked);
                }}
              />
              {t.variant}
              {report.leftOutMissing.length > 0 && (
                <span className="muted small"> {t.variantNote(report.leftOutMissing.length)}</span>
              )}
            </label>
          )}

          <h4>{t.fieldsTitle}</h4>
          <label>
            {t.name}
            <input
              type="text"
              name="name"
              value={name}
              maxLength={128}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <div className="row wrap" role="group" aria-label={t.roles}>
            <span className="library-label">{t.roles}</span>
            {Object.keys(t.roleOptions).map((r) => (
              <button
                key={r}
                type="button"
                className={`chip toggle ${roles.includes(r) ? 'on' : ''}`}
                aria-pressed={roles.includes(r)}
                onClick={() =>
                  setRoles((list) =>
                    list.includes(r) ? list.filter((x) => x !== r) : [...list, r],
                  )
                }
              >
                {t.roleOptions[r]}
              </button>
            ))}
          </div>
          <div className="row wrap" role="group" aria-label={t.tags}>
            <span className="library-label">{t.tags}</span>
            {tags.map((g) => {
              const text = `${eraLabel(eras, g.era)}${
                g.theatre
                  ? ` · ${theatresOf(eras, g.era).find((th) => th.id === g.theatre)?.chip ?? g.theatre}`
                  : ''
              }`;
              return (
                <button
                  key={`${g.era}-${g.theatre ?? ''}`}
                  type="button"
                  className="chip toggle on"
                  aria-label={t.removeTag(text)}
                  onClick={() => setTags((list) => list.filter((x) => x !== g))}
                >
                  {text} <i className="bi bi-x" aria-hidden="true" />
                </button>
              );
            })}
            <select
              aria-label={t.tagEra}
              name="tag-era"
              value={tagEra}
              onChange={(e) => {
                setTagEra(e.target.value);
                setTagTheatre('');
              }}
            >
              <option value="">{t.tagEra}</option>
              {eras.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.label}
                </option>
              ))}
            </select>
            {theatresOf(eras, tagEra).length > 0 && (
              <select
                aria-label={t.tagTheatre}
                name="tag-theatre"
                value={tagTheatre}
                onChange={(e) => setTagTheatre(e.target.value)}
              >
                <option value="">{t.anyTheatre}</option>
                {theatresOf(eras, tagEra).map((th) => (
                  <option key={th.id} value={th.id}>
                    {th.chip}
                  </option>
                ))}
              </select>
            )}
            <button type="button" disabled={!tagEra} onClick={addTag}>
              {t.addTag}
            </button>
          </div>
          <label>
            {t.author}
            <input
              type="text"
              name="author"
              value={author}
              maxLength={255}
              onChange={(e) => setAuthor(e.target.value)}
            />
          </label>
          <p className="muted small">{t.authorHint}</p>
          <label>
            {t.licence}
            <textarea
              name="licence"
              rows={2}
              value={licence}
              maxLength={2000}
              onChange={(e) => setLicence(e.target.value)}
            />
          </label>
          <p className="muted small">{t.licenceHint}</p>

          <h4>{t.anchorsTitle}</h4>
          <p className="muted small">{t.sketchHint}</p>
          <div className="row wrap workshop-anchors">
            <AnchorSketch
              objects={shownObjects}
              anchors={anchors}
              footprint={body.footprint}
              editable
              onMove={move}
            />
            <ul className="list workshop-anchor-list">
              {anchors.map((a) => (
                <li key={`${a.name}-${a.index}`}>
                  <strong>
                    {t.anchorNames[a.name] ?? a.name}
                    {a.name === 'park' ? ` ${a.index + 1}` : ''}
                  </strong>{' '}
                  <span className={`chip ${a.placed ? '' : 'warn'}`}>
                    {a.placed ? (t.anchorHows[a.how] ?? a.how) : t.toPlace}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {problems.length > 0 && (
            <p className="muted small" role="status">
              {t.cannotSave} {problems.join(' · ')}.
            </p>
          )}
          <div className="row wrap">
            <button
              type="button"
              className="primary"
              disabled={busy || problems.length > 0}
              onClick={() => void save()}
            >
              <i className="bi bi-box-arrow-in-down" aria-hidden="true" />{' '}
              {busy ? t.saving : t.save}
            </button>
            <button type="button" onClick={reset} disabled={busy}>
              {t.reset}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
